import { TEMPLATE_VARIABLES } from "./constants";

import type { JSONContent } from "@tiptap/react";
import type {
  InmailTemplateSchemaType,
  MessageTemplateSchemaType,
  NoteTemplateSchemaType,
  TemplateSchemaType,
  VoiceTemplateSchemaType,
} from "./schemas";
import type {
  CreatedTemplate,
  CreatedVoiceTemplate,
  TemplateType,
} from "./types";

export function getEmptyTemplate(templateType: TemplateType) {
  const baseTemplate = { type: templateType, name: "" };

  if (templateType === "VOICE_TEMPLATE") {
    return { ...baseTemplate, attachments: [] } as VoiceTemplateSchemaType;
  } else {
    const commonData = { message: "", ai_descriptors: {} };

    if (templateType === "NOTE_TEMPLATE") {
      return { ...baseTemplate, ...commonData } as NoteTemplateSchemaType;
    } else if (templateType === "MESSAGE_TEMPLATE") {
      return {
        ...baseTemplate,
        ...commonData,
        attachments: [],
      } as MessageTemplateSchemaType;
    } else {
      return {
        ...baseTemplate,
        ...commonData,
        subject: "",
        attachments: [],
      } as InmailTemplateSchemaType;
    }
  }
}

export function parseTemplateText(
  text: string,
  customVariables: { name: string; value: string }[],
  aiDescriptors: Record<string, string>,
) {
  const mentionPattern = /\{\{(.*?)(?:\?(.*?))?\}\}/g;
  const lines = text.split("\n");
  const content: JSONContent[] = [];

  lines.forEach((line) => {
    const paragraph: JSONContent = { type: "paragraph", content: [] };
    let lastIndex = 0;

    line.replace(mentionPattern, (match, p1, p2, offset) => {
      const variableName = p1.trim();
      const fallbackValue = p2 ? p2.trim() : "";

      if (offset > lastIndex) {
        paragraph.content?.push({
          type: "text",
          text: line.slice(lastIndex, offset),
        });
      }

      // AI Variable
      if (variableName.startsWith("AI.")) {
        const key = variableName.replace("AI.", "");
        const description = aiDescriptors[key] || "";

        paragraph.content?.push({
          type: "aiVariable",
          attrs: {
            id: key,
            description,
          },
        });
      } else {
        // Regular or custom variable
        const variable = [...customVariables, ...TEMPLATE_VARIABLES].find(
          ({ value }) => value === `{{${variableName}}}`,
        );

        // skip if not found
        if (!variable) {
          lastIndex = offset;
          return match;
        }

        paragraph.content?.push({
          type: "variable",
          attrs: {
            id: variable.value,
            label: variable.name,
            fallback: fallbackValue || "",
            isCustomVar: !!customVariables.find(
              ({ value }) => value === variable.value,
            ),
          },
        });
      }

      lastIndex = offset + match.length;
      return match;
    });

    // Add remaining text after last match
    if (lastIndex < line.length) {
      paragraph.content?.push({ type: "text", text: line.slice(lastIndex) });
    }

    // Ensure empty paragraphs are included
    if (paragraph.content.length > 0) {
      content.push(paragraph);
    } else {
      content.push({ type: "paragraph" });
    }
  });

  return { type: "doc", content };
}

export function stringifyTemplateContent(doc: JSONContent): string {
  return doc.content
    .map(({ content }) =>
      (content ?? [])
        .map((node) => {
          if (node.type === "variable") {
            return `${node.attrs.fallback ? `${node.attrs.id.slice(0, -2)}?${node.attrs.fallback}}}` : node.attrs.id}`;
          }

          if (node.type === "aiVariable") {
            return `{{AI.${node.attrs.id}}}`;
          }

          return node.text;
        })
        .join(""),
    )
    .join("\n");
}

export function extractAiDescriptors(nodes: JSONContent[]) {
  const aiDescriptions: Record<string, string> = {};

  if (!Array.isArray(nodes)) {
    return aiDescriptions;
  }

  nodes.forEach((node) => {
    if (node && Array.isArray(node.content)) {
      node.content.forEach((item) => {
        if (item && item.type === "aiVariable") {
          aiDescriptions[item.attrs.id] = item.attrs.description;
        }
      });
    }
  });

  return aiDescriptions;
}

export function getTemplateFromCreatedTemplate(
  voiceTemplate: CreatedVoiceTemplate,
): VoiceTemplateSchemaType;

export function getTemplateFromCreatedTemplate(
  nonVoiceTemplate: Exclude<CreatedTemplate, CreatedVoiceTemplate>,
): Exclude<TemplateSchemaType, VoiceTemplateSchemaType>;

export function getTemplateFromCreatedTemplate(
  createdTemplate: CreatedTemplate,
): TemplateSchemaType;

export function getTemplateFromCreatedTemplate(
  createdTemplate: CreatedTemplate,
): TemplateSchemaType {
  const commonData = { name: createdTemplate.name };

  if (createdTemplate.type === "NOTE_TEMPLATE") {
    const { message, ai_descriptors, type } = createdTemplate;
    return {
      ...commonData,
      type,
      message,
      ai_descriptors,
    };
  }

  const { attachments, type } = createdTemplate;

  if (type === "VOICE_TEMPLATE") {
    return { ...commonData, type, attachments };
  }

  const { ai_descriptors, message } = createdTemplate;

  if (type === "INMAIL_TEMPLATE") {
    return {
      ...commonData,
      type,
      attachments,
      subject: createdTemplate.subject,
      ai_descriptors,
      message,
    };
  }
  return {
    ...commonData,
    type,
    attachments,
    ai_descriptors,
    message,
  };
}

export function areObjectsEqual(
  object1: Record<string, unknown>,
  object2: Record<string, unknown>,
) {
  return (
    Object.entries(object1).every(([key, value]) =>
      Array.isArray(object2[key]) && Array.isArray(value)
        ? areArraysEqual(object2[key], value, (item1, item2) => item1 === item2)
        : object2[key] === value,
    ) &&
    Object.entries(object2).every(([key, value]) =>
      Array.isArray(object1[key]) && Array.isArray(value)
        ? areArraysEqual(object1[key], value, (item1, item2) => item1 === item2)
        : object1[key] === value,
    )
  );
}

export function areArraysEqual<Array1Item, Array2Item>(
  array1: Array1Item[],
  array2: Array2Item[],
  isEqual: (item1: Array1Item, item2: Array2Item) => boolean,
) {
  return (
    array1.every((item1) => array2.find((item2) => isEqual(item1, item2))) &&
    array2.every((item1) => array1.find((item2) => isEqual(item2, item1)))
  );
}

// this function doesn't and shouldn't check template names, since it's used in
// places where the template name is irrelevant
export function areTemplatesEqual(
  template1: TemplateSchemaType,
  template2: TemplateSchemaType,
) {
  const templateUnchangedItem: boolean[] = [];

  if (
    template1.type === "INMAIL_TEMPLATE" &&
    template2.type === "INMAIL_TEMPLATE"
  ) {
    templateUnchangedItem.push(template2.subject === template1.subject);
  }
  if (
    template1.type !== "NOTE_TEMPLATE" &&
    template2.type !== "NOTE_TEMPLATE"
  ) {
    templateUnchangedItem.push(
      areArraysEqual(
        template1.attachments,
        template2.attachments,
        (item1, item2) => item1.id === item2.id,
      ),
    );
  }
  if (
    template1.type !== "VOICE_TEMPLATE" &&
    template2.type !== "VOICE_TEMPLATE"
  ) {
    templateUnchangedItem.push(template1.message === template2.message);
    templateUnchangedItem.push(
      areObjectsEqual(template1.ai_descriptors, template2.ai_descriptors),
    );
  }
  return templateUnchangedItem.every((isUnchanged) => !!isUnchanged);
}
